import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { ShockSaleMarquee } from "../components/ShockSale";
import { Button } from "../components/ui/button";
import { ArrowRight, Lightning, ShieldCheck, Truck, Headset } from "@phosphor-icons/react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/2f27b671-032c-497f-978e-f03265d9d4c8/images/067893f82b268924c8a2e717bd2fe66dbf92027b535b4cebb55fda2485706da0.png";

const CATEGORIES = [
  { name: "GPU", label: "Graphics Cards", desc: "RTX & Radeon power" },
  { name: "CPU", label: "Processors", desc: "Intel & AMD" },
  { name: "Laptops", label: "Laptops", desc: "Gaming & Pro" },
  { name: "Monitors", label: "Monitors", desc: "OLED & Ultrawide" },
  { name: "Peripherals", label: "Peripherals", desc: "Keys, mice, audio" },
  { name: "Components", label: "Components", desc: "PSU, SSD, RAM, Cases" },
];

export default function Home() {
  const [deals, setDeals] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchProducts({ is_deal: true, limit: 8 }).then(setDeals).catch(() => {});
    fetchProducts({ sort: "rating", limit: 8 }).then(setFeatured).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grid-bg border-b-2 border-zinc-950" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-zinc-950 text-white px-3 py-1 font-mono uppercase text-xs tracking-[0.2em] mb-6">
              <Lightning size={12} weight="fill" /> New Drop · Q1 2026
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
              BUILD <br />
              <span className="text-[#002FA7]">RELENTLESSLY.</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-700 leading-relaxed max-w-md mb-8">
              The technical marketplace for serious builders. Real-time deals on GPUs,
              CPUs, laptops, and components — straight from the brands that matter.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/products">
                <Button
                  data-testid="hero-shop-button"
                  className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white h-12 px-6 font-bold uppercase tracking-wider"
                >
                  Shop Catalog
                  <ArrowRight size={18} weight="bold" className="ml-2" />
                </Button>
              </Link>
              <Link to="/products?is_deal=true">
                <Button
                  variant="outline"
                  data-testid="hero-deals-button"
                  className="rounded-none border-2 border-zinc-950 hover:bg-zinc-950 hover:text-white h-12 px-6 font-bold uppercase tracking-wider"
                >
                  Daily Deals
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-12 pt-8 border-t border-zinc-200">
              <div>
                <div className="font-mono text-2xl font-bold">21+</div>
                <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500">Brands</div>
              </div>
              <div>
                <div className="font-mono text-2xl font-bold">99.2%</div>
                <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500">Uptime</div>
              </div>
              <div>
                <div className="font-mono text-2xl font-bold">24h</div>
                <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500">Ship Time</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-square bg-zinc-950 border-4 border-zinc-950 relative overflow-hidden">
              <img src={HERO_IMG} alt="Hero" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-[#FF2A00] text-white p-4 font-mono uppercase text-xs tracking-[0.2em]">
                ⚡ Performance Engineered
              </div>
            </div>
            <div className="absolute -top-4 -right-4 bg-white border-2 border-zinc-950 p-3 font-mono text-xs uppercase tracking-wider hidden md:block">
              <div className="text-[10px] text-zinc-500">From</div>
              <div className="text-2xl font-bold text-[#002FA7]">$159</div>
            </div>
          </div>
        </div>
      </section>

      {/* SHOCK SALE MARQUEE */}
      <ShockSaleMarquee />

      {/* CATEGORY GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="categories-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono uppercase text-xs tracking-[0.2em] text-[#002FA7] mb-2">
              01 · CATEGORIES
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Browse the rack.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border border-zinc-200">
          {CATEGORIES.map((cat, idx) => (
            <Link
              key={cat.name}
              to={`/products?category=${cat.name}`}
              data-testid={`category-link-${cat.name}`}
              className={`p-6 hover:bg-zinc-950 hover:text-white transition-colors group ${idx < CATEGORIES.length - 1 ? "border-r border-zinc-200" : ""} ${idx < 3 ? "border-b border-zinc-200 md:border-b-0" : ""}`}
            >
              <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-400 group-hover:text-zinc-500 mb-2">
                0{idx + 1}
              </div>
              <h3 className="font-heading font-bold text-lg mb-1">{cat.label}</h3>
              <p className="text-xs text-zinc-500 group-hover:text-zinc-300">{cat.desc}</p>
              <ArrowRight size={16} weight="bold" className="mt-3 group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </section>

      {/* DEALS */}
      {deals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-zinc-200" data-testid="deals-section">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="font-mono uppercase text-xs tracking-[0.2em] text-[#FF2A00] mb-2 flex items-center gap-2">
                <Lightning size={12} weight="fill" /> 02 · SHOCK DEALS
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Price drops. Now.
              </h2>
            </div>
            <Link to="/products?is_deal=true" className="hidden sm:flex items-center gap-1 font-mono uppercase text-xs tracking-[0.2em] hover:text-[#FF2A00]">
              View all <ArrowRight size={12} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {deals.slice(0, 4).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-zinc-200" data-testid="featured-section">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="font-mono uppercase text-xs tracking-[0.2em] text-[#002FA7] mb-2">
                03 · TOP RATED
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Builder favorites.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* FEATURES */}
      <section className="bg-zinc-950 text-white py-16" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="border-l-2 border-[#FF2A00] pl-4">
            <Truck size={28} weight="duotone" className="mb-3" />
            <h3 className="font-heading font-bold text-lg mb-1">24H DISPATCH</h3>
            <p className="text-sm text-zinc-400">From warehouse to your bench, fast.</p>
          </div>
          <div className="border-l-2 border-[#FF2A00] pl-4">
            <ShieldCheck size={28} weight="duotone" className="mb-3" />
            <h3 className="font-heading font-bold text-lg mb-1">SECURE CHECKOUT</h3>
            <p className="text-sm text-zinc-400">Stripe-powered. PCI compliant.</p>
          </div>
          <div className="border-l-2 border-[#FF2A00] pl-4">
            <Lightning size={28} weight="duotone" className="mb-3" />
            <h3 className="font-heading font-bold text-lg mb-1">LIVE DEALS</h3>
            <p className="text-sm text-zinc-400">Price drops you won't miss.</p>
          </div>
          <div className="border-l-2 border-[#FF2A00] pl-4">
            <Headset size={28} weight="duotone" className="mb-3" />
            <h3 className="font-heading font-bold text-lg mb-1">EXPERT HELP</h3>
            <p className="text-sm text-zinc-400">Builders helping builders.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
